import { toast } from "sonner";
import { useCallback } from "react";
import { syncTwilioNumbersEdge, validateTwilioCredentialsEdge } from "@/integrations/supabase/twilioApi";
import { OnboardingState } from "./types";
import { supabase } from "@/integrations/supabase/client";

export function useOnboardingCredentials(
  onboarding: OnboardingState | null, 
  setOnboarding: (o: OnboardingState | null) => void, 
  patch: (updates: Partial<OnboardingState>) => Promise<boolean>
) {
  const updateTwilio = async (
    sid: string,
    authToken: string,
    options?: { isAgency?: boolean }
  ) => {
    if (!onboarding) return false;
    try {
      let encryptedToken: string | undefined, iv: string | undefined;
      
      if (authToken) {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) {
          throw new Error("Authentication required. Please refresh and try again.");
        }

        const { data, error } = await supabase.functions.invoke('Calie_encrypt_token', {
          body: { authToken, accountSid: sid }
        });

        if (error || !data) {
          throw new Error("Failed to encrypt Twilio token. Please try again.");
        }

        encryptedToken = data.encrypted;
        iv = data.iv;
      }
      
      // Agency: insert to agency creds table
      if (options?.isAgency) {
        const { error: upsertErr } = await supabase
          .from("calie_agency_twilio_creds")
          .upsert(
            {
              tenant_id: onboarding.tenant_id,
              account_sid: sid,
              auth_token_cipher: encryptedToken,
              encryption_iv: iv,
            },
            { onConflict: "tenant_id" }
          );
        if (upsertErr) throw new Error("Failed to store agency Twilio creds");
        
        // update onboarding and tenant for reference
        await supabase
          .from("calie_tenants")
          .update({ twilio_sid: sid, updated_at: new Date().toISOString() })
          .eq("tenant_id", onboarding.tenant_id);
      } else {
        // Business-owner: store to calie_tenants table
        if (authToken) {
          const { error: tenantError } = await supabase
            .from("calie_tenants")
            .update({
              twilio_sid: sid,
              twilio_auth_token: encryptedToken,
              encryption_iv: iv,
              updated_at: new Date().toISOString(),
            })
            .eq("tenant_id", onboarding.tenant_id);
          if (tenantError) throw new Error("Failed to update Twilio credentials");
        } else if (sid) {
          const { error: tenantError } = await supabase
            .from("calie_tenants")
            .update({
              twilio_sid: sid,
              updated_at: new Date().toISOString(),
            })
            .eq("tenant_id", onboarding.tenant_id);
          if (tenantError) throw new Error("Failed to update Twilio SID");
        }
      }

      // PATCH onboarding state then immediately re-fetch 
      await patch({
        twilio_connected_at: new Date().toISOString(),
        current_step: "number",
      });

      // Immediately refetch onboarding state after update to display latest + masked token
      const { data: onboardingData, error: onboardingError } = await supabase
        .from("calie_onboarding_state")
        .select("*")
        .eq("tenant_id", onboarding.tenant_id)
        .maybeSingle();

      const { data: tenantData, error: tenantError } = await supabase
        .from("calie_tenants")
        .select("twilio_sid, twilio_auth_token")
        .eq("tenant_id", onboarding.tenant_id)
        .maybeSingle();

      let twilioAuthTokenMasked = null;
      if (tenantData?.twilio_auth_token) {
        twilioAuthTokenMasked = "**********";
      }

      setOnboarding({
        ...onboarding,
        ...onboardingData,
        twilio_sid: tenantData?.twilio_sid || sid,
        twilio_auth_token: twilioAuthTokenMasked,
        twilio_connected_at: new Date().toISOString(),
        current_step: "number"
      });

      if (sid && authToken) {
        try {
          await syncTwilioNumbersEdge(onboarding.tenant_id, sid);
        } catch (syncErr: any) {
          toast.error("Failed to import numbers", {
            description: syncErr.message || "Please try again or import manually later"
          });
        }
      }
      return true;
    } catch (err: any) {
      throw err;
    }
  };

  const validateTwilioCredentials = useCallback(
    async (sid: string, authToken: string) => {
      try {
        return await validateTwilioCredentialsEdge(sid, authToken);
      } catch (error: any) {
        if (
          error.message !== "Invalid Twilio credentials. Please check and try again." &&
          error.message !== "Invalid format for Twilio credentials."
        ) {
          toast.error(error.message || "Failed to validate Twilio credentials");
        }
        throw error;
      }
    },
    []
  );

  return { updateTwilio, validateTwilioCredentials, syncTwilioNumbers: syncTwilioNumbersEdge };
}
